import { Button, Dialog, Flex, Portal, Separator, Text } from '@chakra-ui/react';
import { PredictionOpenOrder } from '@features/positions/predictions/types';
import { useHref } from 'hooks/useHref';
import { useRouter } from 'next/navigation';
import { memo, useCallback } from 'react';

interface Props {
  order: PredictionOpenOrder | null;
  open: boolean;
  onClose: () => void;
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const PendingOrderDetailDialog = ({ order, open, onClose }: Props) => {
  const { predictionMarketHref } = useHref();
  const router = useRouter();

  const onClickViewMarket = useCallback(() => {
    if (order == null) {
      return;
    }
    const href = predictionMarketHref(order.marketId);
    router.push(href);
    onClose();
  }, [order, predictionMarketHref, router, onClose]);

  const onClickCancel = useCallback(() => {
    // TODO: implement cancel order
    onClose();
  }, [onClose]);

  if (order == null) {
    return null;
  }

  const fillPercent = order.total > 0 ? Math.round((order.filled / order.total) * 100) : 0;

  return (
    <Dialog.Root open={open} onOpenChange={(e) => !e.open && onClose()} size="sm">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="xl">
            <Dialog.Header px={6} pt={6} pb={0}>
              <Dialog.Title fontSize="lg">{order.market}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body px={6} py={4}>
              <Flex direction="column" gap={4}>
                <Flex direction="column" gap={1}>
                  <Text fontSize="sm" color="fg.subtle" fontWeight="medium">
                    Your prediction
                  </Text>
                  <Text fontSize="md" fontWeight="semibold">
                    {order.outcome}
                  </Text>
                </Flex>

                <Separator borderColor="border.light" />

                <Flex gap={6} flexWrap="wrap">
                  <Flex direction="column" gap={1}>
                    <Text fontSize="xs" color="fg.subtle" fontWeight="medium">
                      Filled
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {order.filled} / {order.total} shares ({fillPercent}%)
                    </Text>
                  </Flex>
                  <Flex direction="column" gap={1}>
                    <Text fontSize="xs" color="fg.subtle" fontWeight="medium">
                      Expires
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {formatDate(order.expiration)}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            </Dialog.Body>
            <Dialog.Footer px={6} pb={6} pt={2} gap={3}>
              <Button
                variant="outline"
                size="sm"
                borderRadius="md"
                colorPalette="red"
                onClick={onClickCancel}
              >
                Cancel order
              </Button>
              <Button size="sm" borderRadius="md" onClick={onClickViewMarket}>
                View market
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default memo(PendingOrderDetailDialog);
